import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence, Reorder } from 'framer-motion';
import { FaLock, FaTerminal, FaTrash, FaEdit, FaPlus, FaCheck, FaSync, FaSave } from 'react-icons/fa';

export interface ProjectLog {
  id: string;
  name: string;
  badges: string[];
  description: string;
  date: string;
  published: boolean;
  featured: boolean;
  githubUrl?: string;
  liveUrl?: string;
  imageUrl?: string;
  category?: string;
  order: number;
}

interface AdminDashboardProps {
  projects: ProjectLog[];
  onAdd: (project: Omit<ProjectLog, 'id' | 'order'>) => void;
  onUpdate: (project: ProjectLog) => void;
  onDelete: (id: string) => void;
  onReorder: (projects: ProjectLog[]) => void;
  onClose: () => void;
}

export const AdminDashboard: React.FC<AdminDashboardProps> = ({
  projects,
  onAdd,
  onUpdate,
  onDelete,
  onReorder,
  onClose,
}) => {
  const [passcode, setPasscode] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authError, setAuthError] = useState(false);
  const [authSuccess, setAuthSuccess] = useState(false);
  const [bootSequence, setBootSequence] = useState<string[]>([]);
  const [isBooting, setIsBooting] = useState(false);

  // Form states
  const [isEditing, setIsEditing] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [badgesText, setBadgesText] = useState('');
  const [description, setDescription] = useState('');
  const [date, setDate] = useState('');
  const [published, setPublished] = useState(true);
  const [featured, setFeatured] = useState(false);
  const [githubUrl, setGithubUrl] = useState('');
  const [liveUrl, setLiveUrl] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [category, setCategory] = useState('Full Stack');

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    setAuthError(false);

    // Secure passcode comparison (No hint in UI, plain hash or simple match)
    // Passcode is: "warrior2026" or "warrior"
    if (passcode.toLowerCase() === 'warrior' || passcode.toLowerCase() === 'warrior2026') {
      setAuthSuccess(true);
      setIsBooting(true);
      triggerBootSequence();
    } else {
      setAuthError(true);
      // Reset error state after shake animation
      setTimeout(() => setAuthError(false), 800);
    }
  };

  const triggerBootSequence = () => {
    const lines = [
      "AUTHENTICATION SUCCESSFUL.",
      "INITIALIZING DIRECTORY DECRYPT...",
      "LOADING ENVIRONMENT CONTEXTS...",
      "CONNECTING LOCAL STORAGE PORT...",
      "SYNCING PROJECT DATABASES...",
      "BOOT COMPLETED. OPENING CMS MODULE..."
    ];

    lines.forEach((line, index) => {
      setTimeout(() => {
        setBootSequence((prev) => [...prev, `[system] ${line}`]);
        if (index === lines.length - 1) {
          setTimeout(() => {
            setIsAuthenticated(true);
            setIsBooting(false);
          }, 600);
        }
      }, (index + 1) * 350);
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name || !description || !date) {
      alert('Required fields must be completed.');
      return;
    }

    const badges = badgesText
      .split(',')
      .map((b) => b.trim())
      .filter((b) => b.length > 0);

    if (isEditing) {
      const existing = projects.find((p) => p.id === isEditing);
      onUpdate({
        id: isEditing,
        name,
        badges,
        description,
        date,
        published,
        featured,
        githubUrl,
        liveUrl,
        imageUrl,
        category,
        order: existing ? existing.order : projects.length,
      });
      setIsEditing(null);
    } else {
      onAdd({
        name,
        badges,
        description,
        date,
        published,
        featured,
        githubUrl,
        liveUrl,
        imageUrl,
        category,
      });
    }

    // Reset fields
    setName('');
    setBadgesText('');
    setDescription('');
    setDate('');
    setPublished(true);
    setFeatured(false);
    setGithubUrl('');
    setLiveUrl('');
    setImageUrl('');
    setCategory('Full Stack');
  };

  const handleEditClick = (p: ProjectLog) => {
    setIsEditing(p.id);
    setName(p.name);
    setBadgesText(p.badges.join(', '));
    setDescription(p.description);
    setDate(p.date);
    setPublished(p.published);
    setFeatured(p.featured);
    setGithubUrl(p.githubUrl || '');
    setLiveUrl(p.liveUrl || '');
    setImageUrl(p.imageUrl || '');
    setCategory(p.category || 'Full Stack');
  };

  const handleCancelEdit = () => {
    setIsEditing(null);
    setName('');
    setBadgesText('');
    setDescription('');
    setDate('');
    setPublished(true);
    setFeatured(false);
    setGithubUrl('');
    setLiveUrl('');
    setImageUrl('');
    setCategory('Full Stack');
  };

  // Safe confirm delete using window.confirm
  const handleDeleteClick = (id: string) => {
    if (window.confirm('Delete this project log permanently?')) {
      onDelete(id);
    }
  };

  if (isBooting) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-cyber-black px-4">
        <div className="w-full max-w-lg p-6 bg-cyber-darker border border-cyber-cyan/30 rounded-lg shadow-glow-cyan font-mono text-xs text-cyber-cyan">
          <div className="flex items-center space-x-2 border-b border-cyber-cyan/20 pb-3 mb-4">
            <FaTerminal className="animate-pulse" />
            <span className="font-bold tracking-wider">SYSTEM INITIALIZATION PROTOCOL</span>
          </div>
          <div className="space-y-2 h-48 overflow-y-auto pr-2">
            {bootSequence.map((line, idx) => (
              <motion.div
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                key={idx}
                className="text-left"
              >
                {line}
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-cyber-black/95 backdrop-blur-md px-4">
        <motion.div
          animate={authError ? { x: [-10, 10, -10, 10, 0] } : {}}
          transition={{ duration: 0.4 }}
          className={`w-full max-w-md p-8 border rounded-xl bg-cyber-darker text-center transition-all ${
            authError
              ? 'border-red-500 shadow-[0_0_20px_rgba(239,68,68,0.4)]'
              : 'border-cyber-cyan/25 shadow-glow-cyan'
          }`}
        >
          <div className="flex justify-center mb-4">
            <div className={`p-4 rounded-full border ${authError ? 'border-red-500 text-red-500 bg-red-950/20' : 'border-cyber-cyan/20 text-cyber-cyan bg-cyber-cyan/5'}`}>
              <FaLock size={24} className={authError ? 'animate-bounce' : ''} />
            </div>
          </div>

          <h2 className="text-sm font-mono tracking-widest text-cyber-gray-300 mb-2 uppercase">
            ━━━━━━━━━━━━━━━━━━━━━━━━━━━━
          </h2>
          <h3 className="text-lg font-mono font-bold text-white mb-2">
            PROJECT MANAGEMENT TERMINAL
          </h3>
          <p className="text-xs text-cyber-gray-400 font-mono mb-6">
            Secure Authentication Required
          </p>

          <form onSubmit={handleLogin} className="space-y-4">
            <input
              type="password"
              placeholder="ENTER DECRYPT SECURITY KEY..."
              value={passcode}
              onChange={(e) => setPasscode(e.target.value)}
              className={`w-full px-4 py-3 bg-cyber-black border rounded focus:outline-none text-white font-mono text-center transition duration-300 ${
                authError ? 'border-red-500 focus:border-red-500' : 'border-cyber-purple/40 focus:border-cyber-cyan'
              }`}
            />
            
            {authError && (
              <p className="text-red-500 font-mono text-xs uppercase animate-pulse">
                [ALERT] Access Denied // Invalid Decryption Key
              </p>
            )}

            <div className="flex gap-4 pt-2">
              <button
                type="submit"
                className="flex-1 py-3 bg-cyber-cyan text-black font-mono font-bold hover:bg-white transition duration-300 rounded shadow-glow-cyan-sm text-xs tracking-wider"
              >
                AUTHENTICATE
              </button>
              <button
                type="button"
                onClick={onClose}
                className="flex-1 py-3 bg-cyber-dark border border-cyber-gray-600 font-mono text-cyber-gray-300 hover:border-red-500 hover:text-red-500 transition duration-300 rounded text-xs tracking-wider"
              >
                ABORT
              </button>
            </div>
          </form>
          <h2 className="text-sm font-mono tracking-widest text-cyber-gray-300 mt-6 uppercase">
            ━━━━━━━━━━━━━━━━━━━━━━━━━━━━
          </h2>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-cyber-black/98 backdrop-blur-lg py-10 px-4 md:px-8">
      <div className="max-w-6xl mx-auto border border-cyber-cyan/20 bg-cyber-darker rounded-2xl shadow-glow-cyan overflow-hidden">
        
        {/* Terminal Header */}
        <div className="flex justify-between items-center bg-cyber-dark px-6 py-4 border-b border-cyber-cyan/15">
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 rounded-full bg-cyber-cyan animate-pulse" />
            <span className="font-mono text-xs tracking-widest text-cyber-cyan font-bold">
              ALOK_SINGH@PORTFOLIO_CMS:~#
            </span>
          </div>
          <button
            onClick={onClose}
            className="text-cyber-gray-400 hover:text-red-500 font-mono text-xs transition duration-200"
          >
            [X] EXIT_CMS
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 p-6 lg:p-8">
          
          {/* Left Column: Add / Edit Form */}
          <div className="lg:col-span-5 space-y-6">
            <div className="border border-cyber-purple/20 bg-cyber-black/40 p-6 rounded-xl shadow-glow-purple-sm">
              <h3 className="text-sm font-mono font-bold text-cyber-purple mb-4 flex items-center">
                <span>&gt; </span>
                <span className="ml-2 uppercase tracking-wider">
                  {isEditing ? 'UPDATE_MODULE_SPEC' : 'REGISTER_NEW_MODULE'}
                </span>
              </h3>

              <form onSubmit={handleSubmit} className="space-y-4 text-xs font-mono">
                <div>
                  <label className="block text-cyber-gray-400 mb-1">PROJECT NAME*:</label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    placeholder="Enter project/log title..."
                    className="w-full px-3 py-2 bg-cyber-black border border-cyber-gray-700 rounded focus:border-cyber-cyan text-white focus:outline-none"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-cyber-gray-400 mb-1">CATEGORY:</label>
                    <select
                      value={category}
                      onChange={(e) => setCategory(e.target.value)}
                      className="w-full px-3 py-2 bg-cyber-black border border-cyber-gray-700 rounded focus:border-cyber-cyan text-white focus:outline-none"
                    >
                      <option value="Full Stack">Full Stack</option>
                      <option value="Frontend">Frontend</option>
                      <option value="Backend">Backend</option>
                      <option value="DevOps">DevOps</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-cyber-gray-400 mb-1">DATE / PERIOD*:</label>
                    <input
                      type="text"
                      value={date}
                      onChange={(e) => setDate(e.target.value)}
                      required
                      placeholder="e.g. Oct 2025"
                      className="w-full px-3 py-2 bg-cyber-black border border-cyber-gray-700 rounded focus:border-cyber-cyan text-white focus:outline-none"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-cyber-gray-400 mb-1">TECH TAGS (COMMA SEPARATED):</label>
                  <input
                    type="text"
                    value={badgesText}
                    onChange={(e) => setBadgesText(e.target.value)}
                    placeholder="React, TypeScript, Tailwind CSS, PM2"
                    className="w-full px-3 py-2 bg-cyber-black border border-cyber-gray-700 rounded focus:border-cyber-cyan text-white focus:outline-none"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-cyber-gray-400 mb-1">GITHUB URL:</label>
                    <input
                      type="text"
                      value={githubUrl}
                      onChange={(e) => setGithubUrl(e.target.value)}
                      placeholder="https://github.com/..."
                      className="w-full px-3 py-2 bg-cyber-black border border-cyber-gray-700 rounded focus:border-cyber-cyan text-white focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-cyber-gray-400 mb-1">LIVE DEMO URL:</label>
                    <input
                      type="text"
                      value={liveUrl}
                      onChange={(e) => setLiveUrl(e.target.value)}
                      placeholder="https://..."
                      className="w-full px-3 py-2 bg-cyber-black border border-cyber-gray-700 rounded focus:border-cyber-cyan text-white focus:outline-none"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-cyber-gray-400 mb-1">THUMBNAIL IMAGE PATH:</label>
                  <input
                    type="text"
                    value={imageUrl}
                    onChange={(e) => setImageUrl(e.target.value)}
                    placeholder="e.g. /static/images/project1.png"
                    className="w-full px-3 py-2 bg-cyber-black border border-cyber-gray-700 rounded focus:border-cyber-cyan text-white focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-cyber-gray-400 mb-1">RICH DESCRIPTION*:</label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    required
                    rows={4}
                    placeholder="Details about database optimizations, system architecture..."
                    className="w-full px-3 py-2 bg-cyber-black border border-cyber-gray-700 rounded focus:border-cyber-cyan text-white focus:outline-none"
                  />
                </div>

                {/* Toggles */}
                <div className="flex space-x-6 py-2 border-t border-b border-cyber-gray-700">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={published}
                      onChange={(e) => setPublished(e.target.checked)}
                      className="accent-cyber-cyan"
                    />
                    <span className="text-cyber-gray-300">PUBLISH STATE</span>
                  </label>
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={featured}
                      onChange={(e) => setFeatured(e.target.checked)}
                      className="accent-cyber-purple"
                    />
                    <span className="text-cyber-gray-300">FEATURED TOGGLE</span>
                  </label>
                </div>

                <div className="flex gap-4 pt-2">
                  <button
                    type="submit"
                    className="flex-1 py-2 bg-cyber-cyan text-black font-bold hover:bg-white transition duration-300 rounded shadow-glow-cyan-sm text-xs"
                  >
                    {isEditing ? 'COMMIT_SPECS()' : 'REGISTER_MODULE()'}
                  </button>
                  {isEditing && (
                    <button
                      type="button"
                      onClick={handleCancelEdit}
                      className="py-2 px-4 bg-cyber-dark border border-cyber-gray-600 text-cyber-gray-300 hover:border-red-500 hover:text-red-500 transition duration-300 rounded"
                    >
                      CANCEL
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>

          {/* Right Column: Reorderable List */}
          <div className="lg:col-span-7 space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-sm font-mono font-bold text-cyber-cyan flex items-center">
                <span>[db.modules.sort()]</span>
                <span className="ml-2 text-xs text-cyber-gray-400 font-normal">
                  (Drag logs to reorder hierarchy)
                </span>
              </h3>
            </div>

            <Reorder.Group
              axis="y"
              values={projects}
              onReorder={onReorder}
              className="space-y-3 max-h-[560px] overflow-y-auto pr-2 border border-cyber-cyan/10 rounded-xl p-4 bg-cyber-black/40"
            >
              {projects.map((p) => (
                <Reorder.Item
                  key={p.id}
                  value={p}
                  className="border border-cyber-gray-700 bg-cyber-dark/80 p-4 rounded-lg relative hover:border-cyber-cyan/50 transition duration-200 cursor-grab active:cursor-grabbing"
                >
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <div className="flex items-center space-x-2">
                        <h4 className="font-mono text-white font-bold text-sm">
                          {p.name}
                        </h4>
                        {!p.published && (
                          <span className="px-1.5 py-0.5 bg-red-950/45 border border-red-500/30 text-red-400 font-mono text-[9px] rounded">
                            DRAFT
                          </span>
                        )}
                        {p.featured && (
                          <span className="px-1.5 py-0.5 bg-cyber-purple/10 border border-cyber-purple/30 text-cyber-purple font-mono text-[9px] rounded">
                            FEATURED
                          </span>
                        )}
                      </div>
                      <span className="text-[10px] font-mono text-cyber-gray-500 block mt-1">
                        DATE: {p.date} // CATEGORY: {p.category || 'N/A'}
                      </span>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditClick(p);
                        }}
                        className="p-1.5 bg-cyber-cyan/10 border border-cyber-cyan/30 text-cyber-cyan hover:bg-cyber-cyan hover:text-black rounded transition duration-200"
                        title="Edit Spec"
                      >
                        <FaEdit size={10} />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteClick(p.id);
                        }}
                        className="p-1.5 bg-red-950/20 border border-red-500/30 text-red-400 hover:bg-red-500 hover:text-white rounded transition duration-200"
                        title="Delete Record"
                      >
                        <FaTrash size={10} />
                      </button>
                    </div>
                  </div>

                  <p className="text-[11px] text-cyber-gray-300 mb-2 font-sans line-clamp-2">
                    {p.description}
                  </p>

                  <div className="flex flex-wrap gap-1">
                    {p.badges.map((b, i) => (
                      <span
                        key={i}
                        className="text-[9px] font-mono bg-cyber-black px-1.5 py-0.5 border border-cyber-gray-700 rounded text-cyber-cyan"
                      >
                        {b}
                      </span>
                    ))}
                  </div>
                </Reorder.Item>
              ))}

              {projects.length === 0 && (
                <div className="text-center py-10 font-mono text-cyber-gray-500 text-xs">
                  NO ACTIVE SPECIFICATIONS LOGGED.
                </div>
              )}
            </Reorder.Group>
          </div>
        </div>
      </div>
    </div>
  );
};
